import palette from "assets/theme/base/palette";
import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DefaultProjectCard from "examples/Cards/ProjectCards/DefaultProjectCard";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import Loader from "pages/loader/Loader";
import { useMaterialUIController } from "context";
import { useTranslation } from "react-i18next";
import { call } from "services/axiosService/core";
import * as Sentry from "@sentry/browser";
import MaxuLogo from "assets/images/nav-logo.png";
import ContentPlayerDialog from "examples/Dialog/ContentPlayerDialog";
import NoContent from "pages/NoContent";

const TYPE_CONFIG = {
  video:       { icon: "play_circle", color: "#2196f3", label: "Video" },
  article:     { icon: "article",     color: "#9c27b0", label: "Article" },
  worksheet:   { icon: "edit_note",   color: "#4caf50", label: "Worksheet" },
  infographic: { icon: "bar_chart",   color: "#ff9800", label: "Infographic" },
};

const TAB_TYPES = [null, "video", "article", "worksheet", "infographic", null];
const TAB_LABEL_KEYS = ["activityReport.tabOverview", "activityReport.tabVideos", "activityReport.tabArticles", "activityReport.tabWorksheets", "activityReport.tabInfographics", "activityReport.tabLogins"];

const getAge = (dob) => {
  if (!dob) return null;
  return Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
};

const formatDate = (dateStr) => {
  if (!dateStr) return "–";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "–";
  }
};

const getEngagement = (learner) => {
  if (!learner) return { label: "Low", color: "#f44336", bgcolor: "#fdecea" };
  const score = parseInt(learner.content_count || 0) + parseInt(learner.login_count || 0);
  if (score >= 15) return { label: "High",   color: "#4caf50", bgcolor: "#e8f5e9" };
  if (score >= 5) return { label: "Medium", color: "#ff9800", bgcolor: "#fff3e0" };
  return             { label: "Low",    color: "#f44336", bgcolor: "#fdecea" };
};

const ViewActivity = () => {
  const [controller] = useMaterialUIController();
  const { profile } = controller;
  const { t } = useTranslation();

  const [learnersActivity, setLearnersActivity] = useState([]);
  const [selectedIndex, setSelectedIndex]       = useState(0);
  const [learnerSearch, setLearnerSearch]       = useState("");
  const [menuAnchor, setMenuAnchor]             = useState(null);
  const [activeTab, setActiveTab]               = useState(0);

  const [userMetricsData, setUserMetricsData] = useState(null);
  const [likedContent, setLikedContent]       = useState([]);
  const [dislikedContent, setDislikedContent] = useState([]);
  const [savedContent, setSavedContent]       = useState([]);
  const [viewedContent, setViewedContent]     = useState([]);

  const [loading, setLoading]             = useState(true);
  const [noContent, setNoContent]         = useState(false);
  const [hasChildren, setHasChildren]     = useState(false);
  const [contentItem, setContentItem]     = useState({});
  const [playContent, setPlayContent]     = useState(false);
  const [showAllContent, setShowAllContent] = useState(false);
  const [showAllLearners, setShowAllLearners] = useState(false);

  useEffect(() => {
    if (profile?.id) {
      getLearnersActivity();
      loadUserContentMetrics();
      hasChildrenHandler();
    }
  }, [profile]);

  const hasChildrenHandler = () => {
    if (
      profile?.relationships?.children?.length > 0 ||
      profile?.relationships?.athletes?.length > 0 ||
      profile?.type?.includes("Shared")
    ) setHasChildren(true);
  };

  const getLearnersActivity = () => {
    call(`users/${profile.id}/learnersactivity`, "GET", {})
      .then((response) => {
        if (response.status === 200) {
          const { activity = [] } = response.data;
          if (activity.length === 0) setNoContent(true);
          else setLearnersActivity(activity);
        } else if (response.status === 204) {
          setNoContent(true);
        }
      })
      .catch((error) => { Sentry.captureException(error); setNoContent(true); })
      .finally(() => setLoading(false));
  };

  const loadUserContentMetrics = () => {
    call(`users/${profile.id}/contentmetrics`, "GET", {})
      .then((res) => {
        if (res.status === 200) {
          const data = res.data;
          setUserMetricsData(data);
          setLikedContent(data.likedcontent || []);
          setDislikedContent(data.dislikedcontent || []);
          setViewedContent(data.viewedcontent || []);
          setSavedContent(data.savedcontent || []);
        }
      })
      .catch((err) => Sentry.captureException(err));
  };

  const getContentLinkHandler = (item) => {
    call(`content/${item.title}`, "GET", {})
      .then((res) => {
        if (res.status === 200) { setPlayContent(true); setContentItem(res.data); }
      })
      .catch((err) => Sentry.captureException(err));
  };

  const closeContentPlayer = () => setPlayContent(false);

  const updateMetrics = (metricData) =>
    call(`users/${profile.id}/contentmetrics`, "PUT", { body: metricData })
      .then((res) => { if (res.status === 200) setUserMetricsData(metricData); })
      .catch((err) => Sentry.captureException(err));

  const likeHandler = (title) => {
    const liked    = [...likedContent];
    const disliked = [...dislikedContent];
    if (!liked.includes(title)) liked.push(title);
    const i = disliked.indexOf(title);
    if (i > -1) disliked.splice(i, 1);
    updateMetrics({ ...userMetricsData, likedcontent: liked, dislikedcontent: disliked, eventkey: { event: "likecontent", value: title } });
    setLikedContent(liked);
    setDislikedContent(disliked);
  };

  const removeLikeHandler = (title) => {
    const liked = likedContent.filter((c) => c !== title);
    updateMetrics({ ...userMetricsData, likedcontent: liked, eventkey: { event: "removeLike", value: title } });
    setLikedContent(liked);
  };

  const dislikeHandler = (title) => {
    const liked    = [...likedContent];
    const disliked = [...dislikedContent];
    if (!disliked.includes(title)) disliked.push(title);
    const i = liked.indexOf(title);
    if (i > -1) liked.splice(i, 1);
    updateMetrics({ ...userMetricsData, likedcontent: liked, dislikedcontent: disliked, eventkey: { event: "dislikecontent", value: title } });
    setLikedContent(liked);
    setDislikedContent(disliked);
  };

  const removeDislikeHandler = (title) => {
    const disliked = dislikedContent.filter((c) => c !== title);
    updateMetrics({ ...userMetricsData, dislikedcontent: disliked, eventkey: { event: "removeDislike", value: title } });
    setDislikedContent(disliked);
  };

  const saveHandler = (title) => {
    const saved       = [...savedContent];
    const alreadySaved = saved.includes(title);
    if (alreadySaved) saved.splice(saved.indexOf(title), 1);
    else saved.push(title);
    updateMetrics({ ...userMetricsData, savedcontent: saved, eventkey: { event: alreadySaved ? "removeSaved" : "savedcontent", value: title } });
    setSavedContent(saved);
  };

  // ── Derived values ──
  const selectedLearner = learnersActivity[selectedIndex];

  const contentByType = useMemo(() => {
    if (!selectedLearner) return {};
    return selectedLearner.watched_content.reduce((acc, c) => {
      acc[c.content_type] = (acc[c.content_type] || 0) + 1;
      return acc;
    }, {});
  }, [selectedLearner, selectedIndex]);

  const filteredLearners = learnersActivity.filter((l) =>
    l.name.toLowerCase().includes(learnerSearch.toLowerCase())
  );

  const filteredContent =
    activeTab === 0 || activeTab === 5
      ? selectedLearner?.watched_content || []
      : (selectedLearner?.watched_content || []).filter((c) => c.content_type === TAB_TYPES[activeTab]);

  const engagement = getEngagement(selectedLearner);

  const engagementInsight = () => {
    const firstName = selectedLearner?.name?.split(" ")[0] || "this learner";
    if (engagement.label === "High")   return t("activityReport.engagementHigh", { name: firstName });
    if (engagement.label === "Medium") return t("activityReport.engagementMedium", { name: firstName });
    return t("activityReport.engagementLow", { name: firstName });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar title={t("pageTitles.viewActivity")} />

      {loading && (
        <Dialog open={loading} fullScreen>
          <DialogContent>
            <Loader loaderText={t("loaderText.loadingActivity")} />
          </DialogContent>
        </Dialog>
      )}

      {noContent && !loading && (
        <NoContent
          title={hasChildren ? t("activityReport.noActivityAvailable") : t("activityReport.noChildren")}
          subtitle={hasChildren
            ? t("activityReport.noActivitySubtitle")
            : t("activityReport.noChildrenSubtitle")
          }
          icon={hasChildren ? "bar_chart" : "group_add"}
          iconColor={hasChildren ? "#2196f3" : "#4caf50"}
        />
      )}

      {!noContent && !loading && learnersActivity.length > 0 && (
        <MDBox pt={2}>

          {/* ── Learner Selector Bar ── */}
          <Card sx={{ p: 2.5, mb: 2, borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <MDTypography variant="body2" fontWeight="medium" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                {t("activityReport.selectLearner")}
              </MDTypography>

              <MDBox
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  border: `1.5px solid ${palette.neutral[200]}`, borderRadius: "12px",
                  px: 1.5, py: 0.75, cursor: "pointer", bgcolor: "#fff", minWidth: 200,
                  transition: "border-color 200ms",
                  "&:hover": { borderColor: palette.brand[400] },
                }}
              >
                <Avatar src={selectedLearner?.profile_image_url || ""} sx={{ width: 28, height: 28 }}><Icon sx={{ fontSize: "16px !important" }}>person</Icon></Avatar>
                <MDTypography variant="body2" fontWeight="medium" sx={{ flex: 1 }}>
                  {selectedLearner?.name || "–"}
                </MDTypography>
                <Icon sx={{ fontSize: "20px !important", color: "text.secondary" }}>keyboard_arrow_down</Icon>
              </MDBox>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              {selectedLearner?.dob && (
                <MDBox>
                  <MDTypography variant="caption" color="text.secondary" display="block">{t("activityReport.age")}</MDTypography>
                  <MDTypography variant="body2" fontWeight="bold">{getAge(selectedLearner.dob)}</MDTypography>
                </MDBox>
              )}

              <MDBox>
                <MDTypography variant="caption" color="text.secondary" display="block">{t("activityReport.memberSince")}</MDTypography>
                <MDTypography variant="body2" fontWeight="bold" sx={{ color: palette.brand[600] }}>
                  {formatDate(selectedLearner?.signup_date) || "–"}
                </MDTypography>
              </MDBox>

              <MDBox sx={{ ml: "auto" }}>
                <MDBox
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    border: `1.5px solid ${palette.neutral[200]}`, borderRadius: "12px",
                    px: 1.5, py: 0.75, bgcolor: palette.neutral[50],
                  }}
                >
                  <Icon sx={{ color: palette.brand[500], fontSize: "18px !important" }}>calendar_month</Icon>
                  <MDBox>
                    <MDTypography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                      {t("activityReport.activityReportPeriod")}
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                      {selectedLearner?.start_date || "–"} – {selectedLearner?.end_date || "–"}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>

          {/* ── Learner Dropdown ── */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => { setMenuAnchor(null); setLearnerSearch(""); setShowAllLearners(false); }}
            PaperProps={{ sx: { borderRadius: 3, minWidth: 280, mt: 0.5, boxShadow: "0 8px 32px rgba(0,0,0,0.14)" } }}
          >
            <MDBox px={1.5} pt={1.5} pb={0.75}>
              <MDInput
                fullWidth
                size="small"
                placeholder={t("activityReport.searchLearners")}
                value={learnerSearch}
                onChange={(e) => { setLearnerSearch(e.target.value); setShowAllLearners(false); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon sx={{ fontSize: "18px !important", color: "text.secondary" }}>search</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            {(showAllLearners ? filteredLearners : filteredLearners.slice(0, 5)).map((learner) => {
              const idx        = learnersActivity.indexOf(learner);
              const isSelected = idx === selectedIndex;
              return (
                <MenuItem
                  key={learner.id}
                  onClick={() => {
                    setSelectedIndex(idx);
                    setMenuAnchor(null);
                    setLearnerSearch("");
                    setShowAllLearners(false);
                    setActiveTab(0);
                    setShowAllContent(false);
                  }}
                  sx={{
                    borderRadius: 2, mx: 0.75, my: 0.25,
                    bgcolor: isSelected ? `${palette.brand[500]}14` : "transparent",
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1.5} width="100%">
                    <Avatar src={learner.profile_image_url || ""} sx={{ width: 34, height: 34 }}><Icon sx={{ fontSize: "18px !important" }}>person</Icon></Avatar>
                    <MDTypography variant="body2" fontWeight={isSelected ? "bold" : "regular"} sx={{ flex: 1 }}>
                      {learner.name}
                    </MDTypography>
                    {isSelected && (
                      <Icon sx={{ fontSize: "18px !important", color: palette.brand[500] }}>check_circle</Icon>
                    )}
                  </MDBox>
                </MenuItem>
              );
            })}
            {!showAllLearners && filteredLearners.length > 5 && (
              <MenuItem
                onClick={() => setShowAllLearners(true)}
                sx={{ borderRadius: 2, mx: 0.75, my: 0.25 }}
              >
                <MDBox display="flex" alignItems="center" gap={1.5} width="100%">
                  <MDBox sx={{ width: 34, height: 34, borderRadius: "50%", bgcolor: palette.neutral[100], display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon sx={{ fontSize: "18px !important", color: palette.brand[500] }}>group</Icon>
                  </MDBox>
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: palette.brand[500] }}>
                    {t("activityReport.moreLearners", { count: filteredLearners.length - 5 })}
                  </MDTypography>
                  <Icon sx={{ fontSize: "16px !important", color: palette.brand[500], ml: "auto" }}>expand_more</Icon>
                </MDBox>
              </MenuItem>
            )}
          </Menu>

          {selectedLearner && (
            <>
              {/* ── Profile Card ── */}
              <Card sx={{ p: 3, mb: 2, borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm="auto">
                    <Avatar
                      src={selectedLearner.profile_image_url || ""}
                      alt={selectedLearner.name}
                      sx={{ width: 100, height: 100, borderRadius: "16px", bgcolor: palette.brand[50], fontSize: "2.5rem" }}
                    >
                      <Icon sx={{ fontSize: "48px !important", color: palette.brand[400] }}>person</Icon>
                    </Avatar>
                  </Grid>

                  <Grid item xs={12} sm>
                    <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                      <MDTypography variant="h5" fontWeight="bold">{selectedLearner.name}</MDTypography>
                      {selectedLearner.status && (
                        <MDBox sx={{
                          bgcolor: selectedLearner.status.toLowerCase() === "active" ? "#e8f5e9" : "#f5f5f5",
                          borderRadius: "20px", px: 1.5, py: 0.25,
                        }}>
                          <MDTypography variant="caption" fontWeight="bold" sx={{
                            color: selectedLearner.status.toLowerCase() === "active" ? "#4caf50" : "#9e9e9e",
                            textTransform: "capitalize",
                          }}>
                            {selectedLearner.status}
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                    <MDTypography variant="body2" color="text.secondary" mb={0.75}>
                      {t("activityReport.memberSinceDate", { date: formatDate(selectedLearner.signup_date) })}
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                      "{t("activityReport.motivationalQuote")}"
                    </MDTypography>
                  </Grid>

                  <Grid item xs={12} sm="auto">
                    <MDBox display="flex" gap={2}>
                      <MDBox sx={{ border: `1px solid ${palette.neutral[200]}`, borderRadius: 2, px: 2.5, py: 1.5, textAlign: "center", minWidth: 110 }}>
                        <Icon sx={{ color: "#ff9800", fontSize: "28px !important", mb: 0.25 }}>local_fire_department</Icon>
                        <MDTypography variant="h4" fontWeight="bold">{selectedLearner.login_count || 0}</MDTypography>
                        <MDTypography variant="caption" color="text.secondary">{t("activityReport.totalLogins")}</MDTypography>
                      </MDBox>
                      <MDBox sx={{ border: `1px solid ${palette.neutral[200]}`, borderRadius: 2, px: 2.5, py: 1.5, textAlign: "center", minWidth: 130 }}>
                        <MDBox display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={0.5}>
                          <MDBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#4caf50" }} />
                          <MDTypography variant="caption" color="text.secondary">{t("activityReport.lastActive")}</MDTypography>
                        </MDBox>
                        <MDTypography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                          {formatDate(selectedLearner.last_activity)}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </Grid>
                </Grid>
              </Card>

              {/* ── Stats Row ── */}
              <Card sx={{ p: 3, mb: 2, borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={3} md={2}>
                    <MDBox
                      component="img"
                      src={selectedLearner.learner_animal || MaxuLogo}
                      alt=""
                      sx={{ width: "100%", maxWidth: 140, objectFit: "contain", display: "block", mx: "auto" }}
                    />
                  </Grid>
                  {[
                    { label: t("activityReport.articlesRead"),       value: contentByType["article"]     || 0, icon: "article",     color: "#9c27b0" },
                    { label: t("activityReport.worksheetsViewed"),  value: contentByType["worksheet"]   || 0, icon: "edit_note",   color: "#4caf50" },
                    { label: t("activityReport.videosWatched"),     value: contentByType["video"]       || 0, icon: "play_circle", color: "#2196f3" },
                    { label: t("activityReport.infographicsViewed"),value: contentByType["infographic"] || 0, icon: "bar_chart",   color: "#ff9800" },
                    { label: t("activityReport.loginsLabel"),       value: parseInt(selectedLearner.login_count) || 0, icon: "login", color: "#607d8b" },
                  ].map((stat) => (
                    <Grid item xs={6} sm key={stat.label}>
                      <MDBox textAlign="center">
                        <MDBox sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: `${stat.color}18`, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1 }}>
                          <Icon sx={{ color: stat.color, fontSize: "26px !important" }}>{stat.icon}</Icon>
                        </MDBox>
                        <MDTypography variant="h4" fontWeight="bold">{stat.value}</MDTypography>
                        <MDTypography variant="caption" color="text.secondary">{stat.label}</MDTypography>
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>
              </Card>

              {/* ── Activity Info Row ── */}
              <Card sx={{ p: 3, mb: 2, borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <MDBox display="flex" alignItems="flex-start" gap={1}>
                      <Icon sx={{ color: palette.brand[500], fontSize: "20px !important", mt: 0.25 }}>calendar_today</Icon>
                      <MDBox>
                        <MDTypography variant="caption" color="text.secondary" display="block">{t("activityReport.lastLogin")}</MDTypography>
                        <MDTypography variant="body2" fontWeight="bold">{formatDate(selectedLearner.last_activity)}</MDTypography>
                      </MDBox>
                    </MDBox>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <MDBox display="flex" alignItems="flex-start" gap={1}>
                      <Icon sx={{ color: palette.brand[500], fontSize: "20px !important", mt: 0.25 }}>trending_up</Icon>
                      <MDBox>
                        <MDTypography variant="caption" color="text.secondary" display="block">{t("activityReport.mostActiveDay")}</MDTypography>
                        <MDTypography variant="body2" fontWeight="bold">{formatDate(selectedLearner.most_active_day)}</MDTypography>
                      </MDBox>
                    </MDBox>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <MDBox display="flex" alignItems="flex-start" gap={1}>
                      <Icon sx={{ color: "#ff9800", fontSize: "20px !important", mt: 0.25 }}>star</Icon>
                      <MDBox>
                        <MDTypography variant="caption" color="text.secondary" display="block">{t("activityReport.engagementStatus")}</MDTypography>
                        <MDBox sx={{ display: "inline-flex", mt: 0.5, bgcolor: engagement.bgcolor, borderRadius: "20px", px: 1.5, py: 0.2 }}>
                          <MDTypography variant="caption" fontWeight="bold" sx={{ color: engagement.color }}>
                            {t(`activityReport.engagementLevel${engagement.label}`)}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <MDBox display="flex" alignItems="flex-start" gap={1}>
                      <MDBox sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#4caf50", mt: 0.6, flexShrink: 0 }} />
                      <MDBox>
                        <MDTypography variant="caption" color="text.secondary" display="block">{t("activityReport.engagementInsight")}</MDTypography>
                        <MDTypography variant="body2" sx={{ lineHeight: 1.5 }}>{engagementInsight()}</MDTypography>
                      </MDBox>
                    </MDBox>
                  </Grid>
                </Grid>
              </Card>

              {/* ── All Reports ── */}
              <Card sx={{ p: 3, mb: 2, borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <MDTypography variant="h6" fontWeight="bold" mb={2}>{t("activityReport.allReports")}</MDTypography>

                {/* Custom tab pills */}
                <MDBox sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3, p: 0.75, bgcolor: palette.neutral[100], borderRadius: 2, width: "fit-content" }}>
                  {TAB_LABEL_KEYS.map((labelKey, i) => (
                    <MDBox
                      key={labelKey}
                      onClick={() => setActiveTab(i)}
                      sx={{
                        px: 2, py: 0.75, borderRadius: "10px", cursor: "pointer",
                        bgcolor: activeTab === i ? palette.brand[500] : "transparent",
                        transition: "all 200ms",
                      }}
                    >
                      <MDTypography
                        variant="body2"
                        fontWeight={activeTab === i ? "bold" : "regular"}
                        sx={{ color: activeTab === i ? "#fff" : "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {t(labelKey)}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>

                {/* ── Overview tab ── */}
                {activeTab === 0 && (
                  <>
                    <Grid container spacing={2} mb={3}>
                      {[
                        { label: t("activityReport.totalLogins"),      value: parseInt(selectedLearner.login_count), icon: "login",    color: palette.brand[500], sub: t("activityReport.loginsLabel") },
                        { label: t("activityReport.completedContent"), value: parseInt(selectedLearner.content_count), icon: "verified",     color: "#4caf50", sub: t("activityReport.hundredPercentCompleted") },
                        { label: t("activityReport.totalActivities"),  value: parseInt(selectedLearner.content_count) + parseInt(selectedLearner.login_count), icon: "groups", color: "#2196f3", sub: t("activityReport.contentsAndLogins") },
                        // { label: "In Progress",           value: 0,                                       icon: "access_time",  color: "#ff9800", sub: "0% in progress" },
                        // { label: "Not Started",           value: 0,                                       icon: "timelapse",    color: "#9e9e9e", sub: "0% not started" },
                      ].map((stat) => (
                        <Grid item xs={6} sm={4} md key={stat.label}>
                          <MDBox sx={{ border: `1px solid ${palette.neutral[200]}`, borderRadius: 2, p: 2, textAlign: "center", height: "100%" }}>
                            <Icon sx={{ color: stat.color, fontSize: "30px !important", mb: 0.5 }}>{stat.icon}</Icon>
                            <MDTypography variant="h5" fontWeight="bold">{stat.value}</MDTypography>
                            <MDTypography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }}>{stat.label}</MDTypography>
                            {stat.sub && <MDTypography variant="caption" sx={{ color: "text.disabled" }}>{stat.sub}</MDTypography>}
                          </MDBox>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Next Watched Content + Summary */}
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <MDTypography variant="subtitle1" fontWeight="bold" mb={1.5}>{t("activityReport.nextWatchedContent")}</MDTypography>
                        <MDBox sx={{ border: `1px solid ${palette.neutral[200]}`, borderRadius: 2, overflow: "hidden" }}>
                          {filteredContent.length === 0 ? (
                            <MDTypography variant="body2" color="text.secondary" textAlign="center" py={3}>
                              {t("activityReport.noContentViewedYet")}
                            </MDTypography>
                          ) : (
                            filteredContent.slice(0, 4).map((c, i, arr) => {
                              const cfg = TYPE_CONFIG[c.content_type] || TYPE_CONFIG["article"];
                              return (
                                <MDBox key={i}>
                                  <MDBox
                                    display="flex" alignItems="center" gap={1.5} px={2} py={1.5}
                                    sx={{ cursor: "pointer", "&:hover": { bgcolor: palette.neutral[50] } }}
                                    onClick={() => getContentLinkHandler(c)}
                                  >
                                    <MDBox sx={{ width: 36, height: 36, borderRadius: "8px", bgcolor: `${cfg.color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                      <Icon sx={{ color: cfg.color, fontSize: "18px !important" }}>{cfg.icon}</Icon>
                                    </MDBox>
                                    <MDTypography variant="body2" sx={{ flex: 1, lineHeight: 1.3 }}>{c.title}</MDTypography>
                                    <MDBox sx={{ bgcolor: `${cfg.color}18`, borderRadius: "20px", px: 1, py: 0.25, flexShrink: 0 }}>
                                      <MDTypography variant="caption" fontWeight="bold" sx={{ color: cfg.color, textTransform: "uppercase", fontSize: "0.6rem", letterSpacing: "0.04em" }}>
                                        {c.content_type}
                                      </MDTypography>
                                    </MDBox>
                                    <Icon sx={{ color: "text.secondary", fontSize: "18px !important" }}>chevron_right</Icon>
                                  </MDBox>
                                  {i < arr.length - 1 && <Divider sx={{ m: 0 }} />}
                                </MDBox>
                              );
                            })
                          )}
                        </MDBox>
                        {filteredContent.length > 4 && (
                          <MDBox
                            display="flex" alignItems="center" gap={0.5} mt={1.5}
                            sx={{ cursor: "pointer" }}
                            onClick={() => setShowAllContent((prev) => !prev)}
                          >
                            <MDTypography variant="body2" fontWeight="bold" sx={{ color: palette.brand[500] }}>
                              {showAllContent ? t("activityReport.hideContent") : t("activityReport.viewAllWatchedContent")}
                            </MDTypography>
                            <Icon sx={{ fontSize: "18px !important", color: palette.brand[500] }}>
                              {showAllContent ? "keyboard_arrow_up" : "arrow_forward"}
                            </Icon>
                          </MDBox>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDTypography variant="subtitle1" fontWeight="bold" mb={1.5}>{t("activityReport.summary")}</MDTypography>
                        <MDBox sx={{ border: `1px solid ${palette.neutral[200]}`, borderRadius: 2, overflow: "hidden" }}>
                          {[
                            { label: t("activityReport.totalVideosWatched"),      icon: "play_circle", color: "#2196f3",          count: contentByType["video"]       || 0 },
                            { label: t("activityReport.totalArticlesRead"),       icon: "article",     color: "#9c27b0",          count: contentByType["article"]     || 0 },
                            { label: t("activityReport.totalWorksheetsViewed"),   icon: "edit_note",   color: "#4caf50",          count: contentByType["worksheet"]   || 0 },
                            { label: t("activityReport.totalInfographicsViewed"), icon: "bar_chart",   color: "#ff9800",          count: contentByType["infographic"] || 0 },
                            { label: t("activityReport.totalContentsViewed"),     icon: "grid_view",   color: palette.brand[500], count: parseInt(selectedLearner.content_count)  || 0 },
                            { label: t("activityReport.totalLogins"),             icon: "login",       color: "#607d8b",          count: parseInt(selectedLearner.login_count)    || 0 }
                          ].map((row, i, arr) => (
                            <MDBox key={row.label}>
                              <MDBox display="flex" alignItems="center" gap={1.5} px={2} py={1.25}>
                                <MDBox sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: `${row.color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Icon sx={{ color: row.color, fontSize: "16px !important" }}>{row.icon}</Icon>
                                </MDBox>
                                <MDTypography variant="body2" sx={{ flex: 1 }}>{row.label}</MDTypography>
                                <MDTypography variant="body2" fontWeight="bold">{row.count}</MDTypography>
                              </MDBox>
                              {i < arr.length - 1 && <Divider sx={{ m: 0 }} />}
                            </MDBox>
                          ))}
                        </MDBox>
                      </Grid>
                    </Grid>

                    {/* Expanded all-content grid */}
                    {showAllContent && filteredContent.length > 0 && (
                      <MDBox mt={3}>
                        <Divider sx={{ mb: 2 }} />
                        <MDTypography variant="subtitle1" fontWeight="bold" mb={2}>
                          {t("activityReport.allWatchedContent", { count: filteredContent.length })}
                        </MDTypography>
                        <Grid container spacing={3}>
                          {filteredContent.map((item, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                              <DefaultProjectCard
                                item={item}
                                title={item.title}
                                type={item.content_type}
                                image={item.thumbnail_url || MaxuLogo}
                                liked={likedContent.includes(item.title)}
                                disliked={dislikedContent.includes(item.title)}
                                saved={savedContent.includes(item.title)}
                                viewed={viewedContent.includes(item.title)}
                                viewedChip={viewedContent.includes(item.title)}
                                getContentLinkHandler={getContentLinkHandler}
                                likeHandler={likeHandler}
                                removeLikeHandler={removeLikeHandler}
                                dislikeHandler={dislikeHandler}
                                removeDislikeHandler={removeDislikeHandler}
                                saveHandler={saveHandler}
                                action={{
                                  type: "external",
                                  route: item.content_url,
                                  color: "info",
                                  label: t("buttonTitles.view"),
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </MDBox>
                    )}
                  </>
                )}

                {/* ── Filtered content tabs: Videos / Articles / Worksheets / Infographics ── */}
                {activeTab >= 1 && activeTab <= 4 && (
                  <Grid container spacing={3}>
                    {filteredContent.length === 0 ? (
                      <Grid item xs={12}>
                        <MDBox textAlign="center" py={5}>
                          <Icon sx={{ fontSize: "48px !important", color: palette.neutral[300], mb: 1 }}>
                            {TYPE_CONFIG[TAB_TYPES[activeTab]]?.icon || "folder_open"}
                          </Icon>
                          <MDTypography variant="body2" color="text.secondary">
                            {t("activityReport.noContentTypeViewed", { type: TAB_TYPES[activeTab] })}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    ) : (
                      filteredContent.map((item, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                          <DefaultProjectCard
                            item={item}
                            title={item.title}
                            type={item.content_type}
                            image={item.thumbnail_url || MaxuLogo}
                            liked={likedContent.includes(item.title)}
                            disliked={dislikedContent.includes(item.title)}
                            saved={savedContent.includes(item.title)}
                            viewed={viewedContent.includes(item.title)}
                            viewedChip={viewedContent.includes(item.title)}
                            getContentLinkHandler={getContentLinkHandler}
                            likeHandler={likeHandler}
                            removeLikeHandler={removeLikeHandler}
                            dislikeHandler={dislikeHandler}
                            removeDislikeHandler={removeDislikeHandler}
                            saveHandler={saveHandler}
                            action={{
                              type: "external",
                              route: item.content_url,
                              color: "info",
                              label: t("buttonTitles.view"),
                            }}
                          />
                        </Grid>
                      ))
                    )}
                  </Grid>
                )}

                {/* ── Logins tab ── */}
                {activeTab === 5 && (
                  <MDBox textAlign="center" py={5}>
                    <MDBox sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: "#607d8b18", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
                      <Icon sx={{ fontSize: "40px !important", color: "#607d8b" }}>login</Icon>
                    </MDBox>
                    <MDTypography variant="h3" fontWeight="bold">{selectedLearner.login_count}</MDTypography>
                    <MDTypography variant="body2" color="text.secondary" mt={1}>
                      {t("activityReport.totalLoginsBetween", { startDate: selectedLearner.start_date, endDate: selectedLearner.end_date })}
                    </MDTypography>
                  </MDBox>
                )}
              </Card>

              {/* Footer note */}
              <MDBox display="flex" alignItems="center" gap={1} pb={3} px={1}>
                <Icon sx={{ fontSize: "16px !important", color: "text.disabled" }}>info</Icon>
                <MDTypography variant="caption" color="text.secondary">
                  {t("activityReport.dataShownFor", { startDate: selectedLearner.start_date, endDate: selectedLearner.end_date })}
                </MDTypography>
              </MDBox>
            </>
          )}
        </MDBox>
      )}

      {playContent && (
        <ContentPlayerDialog
          playContent={playContent}
          closeContentPlayer={closeContentPlayer}
          content={contentItem}
          userMetricsData={userMetricsData}
          setUserMetricData={setUserMetricsData}
          setViewedContent={setViewedContent}
          userId={profile.id}
          t={t}
        />
      )}
    </DashboardLayout>
  );
};

export default ViewActivity;
